import React, { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import _isEqual from 'lodash/isEqual';
import { isSameDay, format } from 'date-fns';

import { RANGES } from 'components/DateRangePicker/ranges';
import LoaderBar from 'components/LoaderBar';
import DashboardContainer from 'components/DashboardContainer';
import TableTopFilters from 'components/TableTopFilters';
import SearchWithFilters from 'components/SearchWithFilters';
import PayoutTable from './PayoutTable';

import { DATE_FOR_API } from 'utils/dateFormat';
import { usePrevious } from 'hooks';
import { payoutsTable as TEXT } from 'locales/en/payout';
import {
  payoutAllSelector,
  setParams,
  getPayouts,
  searchPayouts,
  setSearchTerm,
  exportPayouts,
  resetSearch,
} from 'redux/slices/payout';
import { STATUS_FILTERS } from './helpers';
import schema, { FIELD_NAMES, defaultValues as _defaultValues } from './schema';

const Payouts = () => {
  const dispatch = useDispatch();
  const { data, params, pagination, isLoading, searchTerm } = useSelector(
    (state) => payoutAllSelector(state)
  );
  const company = useSelector((state) => state.company.current);
  const prevCompany = usePrevious(company);
  const prevParams = usePrevious(params);
  const prevSearchTerm = usePrevious(searchTerm);

  useEffect(() => {
    dispatch(getPayouts());

    return () => {
      dispatch(resetSearch());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (prevCompany !== null && prevCompany !== company) {
      dispatch(
        setParams({
          page: 1,
        })
      );
      dispatch(getPayouts());
    }
  }, [company, dispatch, prevCompany]);

  useEffect(() => {
    if (
      (prevParams !== null && !_isEqual(params, prevParams)) ||
      (!!prevSearchTerm && searchTerm === '')
    ) {
      dispatch(getPayouts());
    }
  }, [dispatch, params, prevParams, prevSearchTerm, searchTerm]);

  const onChangePerPage = (per_page) => {
    dispatch(
      setParams({
        per_page,
        page: 1,
      })
    );
  };

  const onApplyFilter = (data) => {
    const { statusCheckbox, status } = data;
    let statusSet = new Set();

    for (const property in status) {
      if (status[property]) {
        statusSet.add(property);
      }
    }
    const statusFilter = Array.from(statusSet).join(',');
    let params = {
      statusCheckbox,
      status: statusFilter,
    };

    dispatch(setParams({ ...params, page: 1 }));
  };

  const onApplyDate = (date) => {
    dispatch(
      setParams({
        from: format(date.startDate, DATE_FOR_API),
        to: format(date.endDate, DATE_FOR_API),
        page: 1,
      })
    );
  };

  const onChangePage = ({ selected }) => {
    dispatch(
      setParams({
        page: selected + 1,
      })
    );
  };

  const range = useMemo(() => {
    let id =
      RANGES.find(
        (item) =>
          isSameDay(new Date(item.range().startDate), new Date(params.from)) &&
          isSameDay(new Date(item.range().endDate), new Date(params.to))
      )?.range()?.id || RANGES[RANGES.length - 1]?.range()?.id;

    return {
      startDate: new Date(params.from),
      endDate: new Date(params.to),
      id,
      key: 'selection',
    };
  }, [params.from, params.to]);

  const onChangeSearchTerm = (event) => {
    dispatch(setSearchTerm(event.target.value || ''));
  };

  const onClearInput = () => {
    dispatch(resetSearch());
  };

  const onSearch = () => dispatch(searchPayouts());
  const onExport = () => dispatch(exportPayouts());

  return (
    <DashboardContainer header={TEXT.header}>
      <LoaderBar isVisible={isLoading} />
      <SearchWithFilters
        onChangeInput={onChangeSearchTerm}
        searchTerm={searchTerm}
        onSearch={onSearch}
        placeholder="Search Payout ID"
        onClearInput={onClearInput}
      />
      <PayoutTable
        topContent={
          <TableTopFilters
            onApplyFilter={onApplyFilter}
            statusFilter={STATUS_FILTERS}
            defaultValues={_defaultValues(params)}
            schema={schema}
            fieldNames={FIELD_NAMES}
            onApplyDate={onApplyDate}
            range={range}
            onConfirmExport={onExport}
            isExportDisabled={!data.length}
          />
        }
        data={data}
        title={null}
        pagination={pagination}
        onClickPerPage={onChangePerPage}
        onPageChange={onChangePage}
      />
    </DashboardContainer>
  );
};

export default Payouts;
